import { Column } from "@tanstack/react-table"
import { useEffect, useMemo, useState } from "react"
import { Form, InputGroup } from "react-bootstrap"
import { FilterFn } from "@tanstack/react-table"
import "./DataGrid.css"
export function Filters<T extends object>({
  column,
  manual = false,
  searchPlaceholder ="Search by column"
}: {
  column: Column<T, unknown>
  manual?: boolean
  searchPlaceholder?: string
}) {
  // Filtres definits a meta
  const predefinedFilters =
    (column.columnDef.meta?.filterOptions as { label: string; value: string | number }[]) || []

  let filters: { label: string; value: string | number }[] = []

  if (predefinedFilters.length > 0) {
    filters = predefinedFilters
  } else if (!manual) {
    const facetedValues = column
      .getFacetedRowModel()
      .flatRows.map((row) => row.getValue(column.id))
      .filter((v): v is string | number => typeof v === "string" || typeof v === "number")

    filters = Array.from(new Set(facetedValues))
      .map((v) => ({
        label: String(v ?? ""),
        value: v,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  } else {
    filters = []
  }

  const useMultiSelect = filters.length > 0 || !manual

  return (
    <div className="d-flex">
      {useMultiSelect ? (
        <SelectMultiFilter column={column} filters={filters} searchPlaceholder={searchPlaceholder} />
      ) : (
        <SimpleFilter column={column} searchPlaceholder={searchPlaceholder} />
      )}
    </div>
  )
}

function SelectMultiFilter<T extends object>({
  column,
  filters = [],
  searchPlaceholder
}: {
  column: Column<T, unknown>
  filters: { label: string; value: string | number }[]
  searchPlaceholder: string
}) {
  const rawValue = column.getFilterValue() as (string | number)[] | undefined
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [selected, setSelected] = useState<(string | number)[]>(rawValue ?? filters.map((f) => f.value))

  const displayedOptions = filters.filter((f) =>
    f.label.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const allSelected = useMemo(
    () => displayedOptions.every((opt) => selected.includes(opt.value)),
    [displayedOptions, selected]
  )

  const updateFilter = (newSelected: (string | number)[]) => {
    const filterValue = newSelected.length === filters.length ? undefined : newSelected
    column.setFilterValue(newSelected.length === 0 ? [] : filterValue)
    setSelected(newSelected)
  }

  const toggleSelectAll = () => {
    const newSelected = allSelected
      ? selected.filter((val) => !displayedOptions.some((o) => o.value === val))
      : Array.from(new Set([...selected, ...displayedOptions.map((o) => o.value)]))
    updateFilter(newSelected)
  }

  const toggleOption = (value: string | number) => {
    const newSelected = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value]
    updateFilter(newSelected)
  }

  return (
    <div>
      <InputGroup style={{ width: "200px" }} className="password">
        <Form.Control
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              column.setFilterValue(selected)
            }
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          placeholder={searchPlaceholder}
        />
        {searchTerm && (
          <InputGroup.Text style={{ cursor: "pointer" }} onClick={() => setSearchTerm("")}>
            <i className="bi bi-x"></i>
          </InputGroup.Text>
        )}
      </InputGroup>

      <div className="mt-2" style={{overflowY:'auto', maxHeight: '200px'}}>
        <div className="ps-2">
          <Form.Check type="checkbox" label="Seleccionar tot" checked={allSelected} onChange={toggleSelectAll} />
        </div>
        {displayedOptions.map((opt) => (
          <div className="ps-2" key={opt.value}>
            <Form.Check
              type="checkbox"
              label={opt.label}
              checked={selected.includes(opt.value)}
              onChange={() => toggleOption(opt.value)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}


function SimpleFilter<T extends object>({ column, searchPlaceholder}: { column: Column<T, unknown>, searchPlaceholder: string }) {
    const [value, setValue] = useState<string>((column.getFilterValue() as string) || "")

    return (
        <>
            <InputGroup style={{ width: "200px" }} className='password'>
                <Form.Control
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault()
                            column.setFilterValue(value)
                        }
                    }}
                    onChange={(e) => setValue(e.target.value)}
                    value={value}
                    placeholder={searchPlaceholder}
                />
                {value !== "" && (
                    <InputGroup.Text
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            setValue("")
                            column.setFilterValue("")
                        }}
                    >
                        {" "}
                        <i className='bi bi-x'></i>
                    </InputGroup.Text>
                )}
            </InputGroup>
            <button className='btn ms-1 btn-table' onClick={() => column.setFilterValue(value)}>
                <i className='bi bi-search'></i>
            </button>
        </>
    )
}

export const smartFilter: FilterFn<any> = (row, columnId, filterValue) => {
    const cellValue = row.getValue(columnId)

    if (filterValue == null) return true // sense filtre → incloure tot

    // Si el filtre és un array buit → no volem cap resultat
    if (Array.isArray(filterValue) && filterValue.length === 0) return false

    // Si la cel·la és un array
    if (Array.isArray(cellValue)) {
        if (Array.isArray(filterValue)) {
            return filterValue.some((v) => cellValue.includes(v))
        }
        return cellValue.includes(filterValue)
    }

    // Si la cel·la és un valor simple
    const cell = String(cellValue ?? "").toLowerCase()
    if (Array.isArray(filterValue)) {
        return filterValue.some((v) => cell === String(v).toLowerCase())
    }

    return cell.includes(String(filterValue).toLowerCase())
}

